import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import Box from '@material-ui/core/Box';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import Container from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
// import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import FilterListIcon from '@material-ui/icons/FilterList';
// import axios from 'axios';
// import { Hidden } from '@material-ui/core';
// import fileDownload from 'js-file-download'

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const { classes, onSelectAllClick, order, orderBy, numSelected, documentCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < documentCount}
            checked={documentCount > 0 && numSelected === documentCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all documents' }}
          />
        </TableCell>
        <TableCell padding="checkbox">
          Download
        </TableCell>
        {props.metaData.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'right'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  documentCount: PropTypes.number.isRequired,
  metaData: PropTypes.array.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    textAlign: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
          {numSelected} selected
        </Typography>
      ) : (
        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
          Submitted Documents
        </Typography>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete">
            {/* <DeleteIcon /> */}
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useTableStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    overflow: "hidden",
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 300,
    height: "100%"
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  downloadButton: {
    height: "49px",
    '&:hover': {
      color: theme.palette.primary.dark,
      background: theme.palette.secondary.light
      },
  }
}));

const getPdfUrl = (formID, submissionID, download) => {
  let baseUrl = `${process.env.REACT_APP_PDF_URL}?formId=${formID}&submissionId=${submissionID}`
  if (download && download === true) {
    return baseUrl + '&download=true'
  }
  return baseUrl
}

export default function EnhancedTable({
  documents,
  metaData,
  setPdfUrl,
  setPdfVisible,
  isLoading,
  isError
}) {
  const classes = useTableStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('submissionDate');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [documentsPerPage, setDocumentsPerPage] = useState(10);

  useEffect(() => {
    if (selected.length !== 1) {
      setPdfVisible(false)
    } else {
      setPdfVisible(true)
      const doc = documents.filter(d => {
        return d.eventID === selected[0]
      })[0]
      setPdfUrl(getPdfUrl(doc.formID, doc.submissionID))
    }
  }, [selected, documents, setPdfUrl, setPdfVisible])

  const getPdfDownload = (eventID) => {
    const doc = documents.filter(d => {
      return d.eventID === eventID
    })[0]
    return getPdfUrl(doc.formID, doc.submissionID, true)
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {

    if (event.target.checked) {
      const newSelecteds = documents.map((n) => n.eventID);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, eventID) => {
    const selectedIndex = selected.indexOf(eventID);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, eventID);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeDocumentsPerPage = (event) => {
    setDocumentsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const isSelected = (eventID) => selected.indexOf(eventID) !== -1;

  const emptyDocuments = documentsPerPage - Math.min(documentsPerPage, documents.length - page * documentsPerPage);

  return isLoading ? (<div><h2>Loading</h2></div>) 
  : isError ? (<div><h2>There is a problem fetching the data</h2></div>)
  : (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              documentCount={documents.length}
              metaData={metaData}
            />
            <TableBody>
              {stableSort(documents, getComparator(order, orderBy))
                .slice(page * documentsPerPage, page * documentsPerPage + documentsPerPage)
                .map((document, index) => {
                  const isItemSelected = isSelected(document.eventID);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, document.eventID)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={document.submissionID}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell >
                        <a href={getPdfDownload(document.eventID)}>
                        <Container 
                          id={document.eventID} 
                          className={classes.pdfDownloadButton}
                        >
                          <GetAppIcon className={classes.downloadButton} />
                        </Container>
                        </a>
                      </TableCell>
                      {/* <TableCell component="th" id={labelId} scope="row" padding="none">
                        {document.submissionID}
                      </TableCell> */}
                      <TableCell align="right">{document.taxpayerName}</TableCell>
                      <TableCell align="right">{document.parcelId}</TableCell>
                      <TableCell align="right">{document.submissionDate}</TableCell>
                    </TableRow>
                  );
                })}
              {emptyDocuments > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyDocuments }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={documents.length}
          rowsPerPage={documentsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeDocumentsPerPage}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense padding"
      />
    </div>
  );
}

EnhancedTable.propTypes = {
  documents: PropTypes.array,
  metaData: PropTypes.array,
  setPdfUrl: PropTypes.func,
  setPdfVisible: PropTypes.func,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool
}
